/**
 * Enquête n°16 — Adapté de l'Étude de Cas CCS n°10
 * (Sensibiliser sur les risques spécifiques aux cyclomoteurs.)
 */

import { ShieldCheck, AlertTriangle, Lightbulb, Quote, Users } from 'lucide-react';

export const risquesCycloEnquete = {
  slug: 'risques-cyclo-am',
  themeCode: 'M',
  issueNumber: 16,
  totalIssues: 16,
  series: 'enquetes',
  category: 'Enquête sensibilisation & jeunes',
  shortTitle: 'Risques cyclo',
  title: 'Cyclo à 14 ans : les 4 trappes qui te tuent',
  kicker: 'Enquête n°16 · Série « Témoignages & Sécurité »',
  subtitle:
    "Invisibilité, refus de priorité, perte de contrôle, pression des pairs. 16 % des morts deux-roues motorisés en France ont moins de 18 ans. Cette enquête met à plat les 4 risques spécifiques au cyclo — et comment les neutraliser.",
  heroImage: '/img/magazines/autres-usagers.jpg',
  heroImageAlt: "Motard franchissant un passage piéton en ville — illustration des risques spécifiques aux cyclomoteurs",
  readTimeMin: 8,
  seoDescription:
    "Enquête de sensibilisation sur les 4 risques spécifiques au cyclomoteur pour les 14-17 ans : invisibilité, refus de priorité, perte de contrôle, pression des pairs. 16 % des morts 2RM ont < 18 ans (ONISR).",
  seoKeywords:
    'risques cyclo, 14 ans, ONISR jeunes, refus priorité, débridage, pression pairs, permis AM',

  edito: {
    title: "Mon cyclo, ma responsabilité",
    body: "Tu représentes 2 % du trafic, mais <strong>16 % des tués deux-roues motorisés en France ont moins de 18 ans</strong> (ONISR 2024). À cyclo, tes risques spécifiques ne sont pas ceux d'une voiture, ni même ceux d'une moto. Ils sont propres au combo « cylindrée réduite + conducteur novice + pression sociale forte ». Cette enquête condense l'étude de cas CCS n°10 — sensibilisation aux risques spécifiques cyclomoteur. Pas de morale, pas de discours catastrophe : juste les <strong>4 trappes</strong> qui te tuent, et la façon de les repérer avant qu'elles ne se referment.",
    signedBy: 'La rédaction Code 2 Roues',
  },

  sections: [
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 1',
      title: 'Trappe n°1 : l’invisibilité',
      paragraphs: [
        "Ta silhouette sur un cyclo est <strong>4 à 5 fois plus fine</strong> qu'une voiture. À 50 mètres, dans le rétro d'un conducteur fatigué ou occupé par son téléphone, tu te confonds avec un mur, un poteau, l'arrière-plan. C'est le phénomène dit <strong>« look-but-not-see »</strong> : ses yeux te balayent, son cerveau ne t'enregistre pas.",
        "<strong>Solutions actives</strong> : casque clair (jaune fluo, blanc) — vu 4× plus loin qu'un casque noir. Gilet HV à bord (obligation ≥ 125 cm³), recommandé sur cyclo en nuit. Feu de croisement allumé même de jour (obligatoire sur cyclos neufs depuis 2017). Bandes rétro-réfléchissantes sur sac à dos, casque, blouson.",
      ],
      keyPoints: [
        'Silhouette 4× plus fine qu\'une voiture',
        'Casque clair vu 4× plus loin',
        'Bandes rétro + feu allumé',
      ],
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 2',
      title: 'Trappe n°2 : le refus de priorité',
      paragraphs: [
        "Une voiture s'engage en intersection sans céder la priorité à ton cyclo. <strong>35 % des accidents mortels en cyclo en France</strong>. Causes : 1) « look-but-not-see », 2) sous-estimation de ta vitesse (à 45 km/h tu arrives plus vite qu'un vélo, mais les conducteurs te « traitent » comme un vélo), 3) angles morts SUV.",
        "<strong>Solution unique</strong> : <em>ne fais JAMAIS confiance à ta priorité</em>. À chaque carrefour, suppose que l'autre ne te voit pas. Ralentis. Regarde les yeux du conducteur dans son pare-brise. S'il ne te regarde pas → tu freines plus fort. S'il te regarde → il a vu, tu peux maintenir.",
      ],
      keyPoints: [
        '35 % des morts cyclo = refus de priorité',
        'Ne fais jamais confiance à ta priorité',
        "Regarde les yeux du conducteur en croisement",
      ],
    },
    {
      type: 'didyouknow',
      icon: Lightbulb,
      title: 'Le saviez-vous ?',
      body: "<strong>Le débridage</strong> n'augmente pas seulement ta vitesse — il <strong>multiplie ton risque par 3,5</strong>. Pourquoi ? À 70 km/h en cyclo (débridé), la distance d'arrêt passe de 17 mètres à <strong>40 mètres</strong>. Les freins tambour standard ne sont pas dimensionnés pour cette vitesse — ils chauffent en 3 freinages consécutifs (fading). Les pneus 17 pouces dérapent en virage. Et surtout : <strong>l'assurance refuse toute prise en charge</strong> en cas d'accident, même mort. Pour tes parents : facture complète (50 000-150 000 € pour un accident corporel grave) + casier judiciaire (R317-23, délit jusqu'à 30 000 € + 3 ans). Le copain qui te débride pour 50 € n'est <strong>pas ton ami</strong>.",
    },
    {
      type: 'lesson',
      icon: ShieldCheck,
      partLabel: 'Chapitre 3',
      title: 'Trappes n°3 et n°4 : perte de contrôle & pression des pairs',
      paragraphs: [
        "<strong>Trappe n°3 — Perte de contrôle</strong>. Cause directe : vitesse inadaptée (virage trop rapide, freinage panique en ligne droite), débridage (mécanique fragilisée), pneus usés, périle gras (gazole, gravillons). Conséquence type : <strong>chute solo</strong> sans autre véhicule impliqué. Statistique : 40 % des accidents cyclo sont des chutes solo.",
        "<strong>Trappe n°4 — Pression des pairs</strong>. Effet de groupe (Le Bon, 1895) : ta responsabilité personnelle diminue, ta prise de risque augmente. Tu fais en groupe ce que tu ne ferais JAMAIS seul : courses, slaloms entre voitures, passage à plusieurs sur 1 cyclo, embrouilles avec un automobiliste. Statistique : 60 % des accidents impliquant des cyclos de moins de 18 ans surviennent en groupe de 2+ cyclos.",
        "<strong>Solution combinée</strong>. <em>Règle d'or n°1</em> : si tu sens que tu vas trop vite, c'est que tu vas TROP vite. Réduis. <em>Règle d'or n°2</em> : si en groupe quelqu'un te dit « vas-y, tente », c'est que c'est trop risqué. La phrase magique pour s'en sortir sans humiliation : <strong>« Pas ce soir, ma mère me tue »</strong>. Personne ne discute ça à 14 ans.",
      ],
      keyPoints: [
        '40 % des accidents cyclo = chute solo',
        '60 % impliquent 2+ cyclos (effet groupe)',
        "Règle : si tu doutes, tu ralentis",
      ],
    },
    {
      type: 'trap',
      icon: AlertTriangle,
      title: 'Le piège à éviter',
      body: "« Je sais ce que je fais, je roule depuis 2 mois. » <strong>Piège classique des 14-16 ans.</strong> Les chiffres ONISR : <strong>50 % des accidents corporels en cyclo surviennent dans les 6 premiers mois après l'obtention du permis AM</strong>. Pourquoi ? Phase d'<em>incompétence inconsciente</em> (Dunning-Kruger) : tu sais juste assez pour rouler, pas assez pour repérer le danger. L'expérience réelle ne commence qu'après 1 000-2 000 km de pratique variée (pluie, nuit, ville, route). Conseil : <strong>les 12 premiers mois, applique à la lettre tout ce que t'a enseigné l'école AM</strong>. Même si ça te paraît exagéré. Surtout si ça te paraît exagéré.",
    },
  ],

  keyStat: {
    kicker: 'Le chiffre à retenir',
    big: '16 %',
    middle: 'des morts 2RM',
    label: "en France ont moins de 18 ans, alors que les cyclos représentent moins de 2 % du trafic. Les 4 trappes : invisibilité, refus de priorité, perte de contrôle, pression des pairs.",
    source: 'ONISR — Bilan accidentologie 2RM jeunes 2024',
    accent: '#0891b2',
  },

  glossary: [
    { term: '« Look-but-not-see »', def: 'Phénomène cognitif : les yeux du conducteur balayent le cyclo mais le cerveau ne l\'enregistre pas. Principale cause des refus de priorité.' },
    { term: 'Effet de groupe', def: 'Théorie de Le Bon (1895) : en groupe, responsabilité personnelle baisse et prise de risque augmente. Tu fais en groupe ce que tu ne ferais jamais seul.' },
    { term: 'Chute solo', def: 'Accident sans autre véhicule impliqué. Cause : vitesse inadaptée, périle gras, mécanique. 40 % des accidents cyclo.' },
    { term: 'Incompétence inconsciente', def: 'Phase 1 du modèle d\'apprentissage (Dunning-Kruger) : tu sais juste assez pour rouler, pas assez pour repérer le danger. Typique des 0-6 mois post-permis.' },
    { term: 'Débridage', def: 'Modification du cyclo pour dépasser 45 km/h. DÉLIT (R317-23) : jusqu\'à 30 000 € + 3 ans + confiscation + perte assurance.' },
    { term: 'Engagement personnel', def: 'Théorie de Beauvois & Joule : un acte écrit/signé augmente la probabilité d\'être tenu. Outil ECSR pour ancrer un changement comportemental.' },
  ],

  ecsrCorner: {
    title: 'Côté formateur ECSR',
    intro:
      "L'étude de cas n°10 traite de la sensibilisation aux risques spécifiques au cyclomoteur. Public : 3 candidats AM (14-16 ans) en formation.",
    points: [
      'Public-cible : 3 candidats AM (14-16 ans), 1ʳᵉ exposition véhicule motorisé.',
      'Type d\'intervention : sensibiliser (pas enseigner) — viser un déclic comportemental.',
      'Durée : 1 heure — découverte vidéo + brainstorming + apports avec chiffres ONISR + activité cas pratique + engagement écrit.',
      'Méthode : vidéo « Les Invincibles », brainstorming risques, expositif chiffré, étude de cas en sous-groupes, engagement personnel écrit (théorie Beauvois & Joule).',
      "Résultat attendu : l'élève identifie les 4 risques spécifiques, exprime un engagement personnel sur 1 comportement de sécurité.",
    ],
    seeCasLink: {
      label: "Voir l'étude de cas CCS n°10 complète (jury ECSR)",
      to: '/enseignant/etudes-de-cas',
      testid: 'see-cas-ccs-10',
    },
  },

  quiz: {
    title: 'Teste ta compréhension',
    description:
      "10 questions sur les 4 risques spécifiques au cyclo, le débridage, l'effet de groupe. Réussite minimum : 8/10.",
    themeCode: 'M',
    questionsCount: 10,
    passingScore: 8,
  },

  nextIssue: null,
};

export default risquesCycloEnquete;
